import type {
	Budget,
	Category,
	FocusSession,
	Habit,
	HabitLog,
	StudySession,
	Task,
	Track,
	Transaction,
} from "./db";
import { focusedMinutes } from "./focus-timer";
import { countedSpending, monthOf } from "./ledger";
import { weekProgress } from "./study";
import { addDays, APP_TIMEZONE, localDateKey } from "./time";

// The weekly review's auto summary (RV-3, D51): computed on the client from
// local rows and saved into reviews.summary when the review is completed.

export type WeeklySummary = {
	/** Monday of the week, YYYY-MM-DD. */
	week: string;
	tasks: { done: number; planned: number };
	mustWins: { done: number; set: number };
	/** Periods met vs due: each due day for daily habits, the week for weekly ones. */
	habits: { done: number; due: number };
	/** Counted spending (D42 rule) vs a weekly share of the month's spendable budget. */
	spending: { spent: number; budget: number };
	/** Finished focus sessions that started in the week (all of them, tracked or not). */
	focus: { minutes: number };
	/** Logged study plus focus on tracks, and the sum of active tracks' weekly targets (D59). */
	study: { minutes: number; target: number };
};

const live = <T extends { deleted_at: string | null }>(rows: T[]) =>
	rows.filter((r) => !r.deleted_at);

function daysInMonth(day: string): number {
	const [y, m] = day.split("-").map(Number);
	return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

export function weeklySummary(input: {
	week: string;
	today: string;
	tasks: Task[];
	habits: Habit[];
	logs: HabitLog[];
	minimumDays: ReadonlySet<string>;
	transactions: Transaction[];
	budgets: Budget[];
	categories: Category[];
	focusSessions: FocusSession[];
	studySessions: StudySession[];
	tracks: Track[];
	timeZone?: string;
}): WeeklySummary {
	const tz = input.timeZone ?? APP_TIMEZONE;
	const days = Array.from({ length: 7 }, (_, i) => addDays(input.week, i));
	const inWeek = (d: string | null) => d !== null && d >= days[0] && d <= days[6];
	// Days that have started: the rest of the week cannot count against it yet.
	const elapsed = days.filter((d) => d <= input.today);

	const tasks = live(input.tasks).filter((t) => !t.parent_id && t.status !== "dropped");
	const planned = tasks.filter((t) => inWeek(t.due_date));
	const wins = tasks.filter((t) => inWeek(t.must_win_date));

	const count = new Map<string, number>();
	for (const l of live(input.logs)) count.set(`${l.habit_id}:${l.date}`, l.count);
	let habitsDone = 0;
	let habitsDue = 0;
	for (const h of live(input.habits).filter((x) => !x.archived)) {
		const start = localDateKey(new Date(h.created_at), tz);
		if (h.cadence === "weekly") {
			if (days[6] < start) continue;
			habitsDue++;
			const sum = days.reduce((s, d) => s + (count.get(`${h.id}:${d}`) ?? 0), 0);
			if (sum >= h.target_per_period) habitsDone++;
			continue;
		}
		for (const d of elapsed) {
			if (d < start || (input.minimumDays.has(d) && !h.is_minimum)) continue;
			habitsDue++;
			if ((count.get(`${h.id}:${d}`) ?? 0) >= h.target_per_period) habitsDone++;
		}
	}

	const spendable = new Set(
		input.categories.filter((c) => c.kind === "expense" && c.spendable).map((c) => c.id),
	);
	const spent = live(input.transactions)
		.filter((t) => inWeek(localDateKey(new Date(t.occurred_at), tz)))
		.reduce((s, t) => s + countedSpending(t, spendable), 0);
	// Each day of the week takes 1/n of its own month's budget, so a week that
	// spans two months is budgeted fairly. Floors keep it whole UGX.
	let budget = 0;
	for (const d of days) {
		const monthly = live(input.budgets)
			.filter((b) => b.month === monthOf(d) && spendable.has(b.category_id))
			.reduce((s, b) => s + b.amount, 0);
		budget += Math.floor(monthly / daysInMonth(d));
	}

	// Focus: every finished session that started in the week.
	const focusMinutes = input.focusSessions
		.filter((f) => !f.deleted_at && f.ended_at && inWeek(localDateKey(new Date(f.started_at), tz)))
		.reduce((sum, f) => sum + focusedMinutes(f), 0);
	// Study: per active track, logged study plus focus on that track (D59).
	const activeTracks = input.tracks.filter((t) => !t.deleted_at && t.status === "active");
	const studyMinutes = activeTracks.reduce(
		(sum, t) =>
			sum + weekProgress(t, input.week, input.studySessions, input.focusSessions, tz).minutes,
		0,
	);
	const studyTarget = activeTracks.reduce((sum, t) => sum + t.weekly_target_min, 0);

	return {
		week: input.week,
		tasks: { done: planned.filter((t) => t.status === "done").length, planned: planned.length },
		mustWins: { done: wins.filter((t) => t.status === "done").length, set: wins.length },
		habits: { done: habitsDone, due: habitsDue },
		spending: { spent, budget },
		focus: { minutes: focusMinutes },
		study: { minutes: studyMinutes, target: studyTarget },
	};
}
